import angular from "angular";

import {Calculation,CalcWorktime, HolidayYears, HolidayYear, Dates, AbsencePeriod, Arrays, HolidayBonusPaymentMethod, AbsenceCauseCode} from "@salaxy/core";
import {UiHelpers} from "../../services";

/**
 * Displays leaves for current calculation period.
 */
export class CalcLeavesController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["HolidayYears", "UiHelpers"];

  public annualLeavestoBePaid: AbsencePeriod[] = [];
  public annualLeavesWaitingForPayment: AbsencePeriod[] = [];
  public absencesToUse: AbsencePeriod[] = [];
  public absencesToReject: AbsencePeriod[] = [];

  /** Current calculation. */
  public calc: Calculation;

   /** Current worktime. */
   public worktime: CalcWorktime;

   /** All holiday years for employment */
   public employmentHolidayYears: HolidayYear[] = [];

   /** Current holiday year for employment */
   public currentEmploymentHolidayYear: HolidayYear;

  /**
   * Creates a new CalcReportsController
   * @ignore
   */
  constructor(private fullApi: HolidayYears, private uiHelpers: UiHelpers) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
    this.absencesToUse.push(...this.worktime.absencesPeriods?.filter((x) => x.causeCode !== AbsenceCauseCode.AnnualLeave) || []);
    // generate ids for absences
    this.absencesToUse.forEach((x) => x.id = x.id || "$"+this.newGuid());
    this.absencesToReject.push(...angular.copy(this.absencesToUse));
    // there is no need to load absences, because absences can only be used or not used. No picking from other periods should be possible.
    // probably there is no need to edit absences either

    this.getHolidayYearsForEmployment(this.calc.worker.employmentId).then(() => {
      this.annualLeavestoBePaid = this.worktime.absencesPeriods?.filter((x) => x.causeCode == AbsenceCauseCode.AnnualLeave) || [];
      this.annualLeavesWaitingForPayment = this.currentEmploymentHolidayYear.leaves.planned.map( x => ({
        causeCode: AbsenceCauseCode.AnnualLeave,
        id: x.id,
        period: angular.copy(x.period),
        notes: x.notes
      }));
    });
  }

  /** Returns final absences periods */
  public getAbsencesPeriods() {
    const result = angular.copy(([] as AbsencePeriod[]).concat(...this.annualLeavestoBePaid).concat(...this.absencesToUse));
    // remove absence ids
    result.forEach((x) => {
      if (x.id?.startsWith("$")) {
        delete x.id;
      }
    });
    return result;
  }

  /** Loads holiday years for employment and sets current holiday year */
  public getHolidayYearsForEmployment(employmentId: string): Promise<HolidayYear[]> {
    return this.fullApi.getForEmployment(employmentId).then((result) => {
      this.employmentHolidayYears = result;
      const periodStart = Dates.asDate(this.calc.info.workStartDate || Dates.getToday());
      const year = periodStart > Dates.getDate(Dates.getYear(periodStart), 3, 31) ? Dates.getYear(periodStart) : Dates.getYear(periodStart) - 1;

      this.currentEmploymentHolidayYear = this.employmentHolidayYears.find((holidayYear) => holidayYear.year === year);
      return result;
    });
  }

 /**
  * Moves an item between different payment lists.
  * @param item - The item to be moved.
  * @param target - The target payment list.
  */
  public moveItem(item: any, target:"annualLeavestoBePaid" | "annualLeavesWaitingForPayment") {
    if(target ==="annualLeavesWaitingForPayment") {
      const index = this.annualLeavestoBePaid.indexOf(item);
      if (index > -1) {
        this.annualLeavestoBePaid.splice(index, 1);
      }
    }
    if(target ==="annualLeavestoBePaid") {
      this.annualLeavestoBePaid.push(item);
    }
  }

 /**
  * Moves an item between different payment lists.
  * @param item - The item to be moved.
  * @param target - The target payment list.
  */
   public moveAbsenceItem(item: any, target:"absencesToUse" | "absencesToReject") {
    if(target ==="absencesToReject") {
      const index = this.absencesToUse.indexOf(item);
      if (index > -1) {
        this.absencesToUse.splice(index, 1);
      }
    }
    if(target ==="absencesToUse") {
      this.absencesToUse.push(item);
    }
  }

  /**
   * Determines if a given leave should be disabled based on its type and whether it's in the list of leaves to be paid.
   * @param {AnnualLeave} leave - The leave object to check.
   */
  public isSelectedToBePaid(leave: AbsencePeriod) {
    return this.annualLeavestoBePaid?.some((item) => item.id === leave.id);
  }

  /**
   * Determines if a given absence should be disabled based on it's in the list of absences to use.
   * @param {AbsencePeriod} absence - The leave object to check.
   */
    public isAbsenceSelectedToUse(absence: AbsencePeriod) {
      return this.absencesToUse?.some((item) => item.id === absence.id);
    }

  /**
   * Determines the style for a given leave based on whether it should be disabled or not.
   * @param {AnnualLeave} leave - The leave object to check.
   */
  public getDisabledStyle(leave: AbsencePeriod) {
    if (this.isSelectedToBePaid(leave)) {
      return {
        "background-color": "lightgrey",
        "opacity": "0.5"
      };
    }
  }

  /**
   * Determines the style for a given absence based on whether it should be disabled or not.
   * @param {AnnualLeave} absence - The absences object to check.
   */
    public getAbsenceDisabledStyle(absence: AbsencePeriod) {
      if (this.isAbsenceSelectedToUse(absence)) {
        return {
          "background-color": "lightgrey",
          "opacity": "0.5"
        };
      }
    }

  /** Returns daysCount sum of annualLeavestoBePaid/bonusPeriodstoBePaid depending on type parameter*/
  public getAccrualRemaningsToBePaid(type: "holidayPay" | "holidayBonus") {
    return type === "holidayPay" ? Arrays.sum(this.annualLeavestoBePaid, (x) => x.period.daysCount) || 0 : Arrays.sum(this.annualLeavestoBePaid, (x) => x.bonusDaysCount) || 0;
  }

  /**
   * Shows the edit dialog.
   * @param item AnnualLeave to edit.
   */
  public showEditDialog(item: AbsencePeriod) {
    if (!item) {
      throw new Error("showEditDialog called without a parameter.");
    }

    const logic =  {
      /** Updates the workdays count */
      updatePeriodDays: (current: AbsencePeriod) => {
        if (current.period.start > current.period.end) {
          current.period.end = current.period.start;
        }
        if (current.period.start && current.period.end) {
          if (current.period.days) {
            current.period.days = Dates.getVacationDays(current.period.start, current.period.end);
            current.period.daysCount = current.period.days.length;
            if (this.worktime.holidaySpec.bonusMethod !== HolidayBonusPaymentMethod.None) {
              current.bonusDaysCount = current.period.days.length;
            }
          } else {
            current.period.daysCount = Dates.getVacationDays(current.period.start, current.period.end).length;
            if (this.worktime.holidaySpec.bonusMethod !== HolidayBonusPaymentMethod.None) {
              current.bonusDaysCount = current.period.daysCount;
            }
          }
        }
      },

      /** Sets the days array in period to enable days selection UI. */
      setDaysSelection: (period: AbsencePeriod) => {
        period.period.days = Dates.getVacationDays(period.period.start, period.period.end);
        period.period.daysCount = period.period.days.length;
        if (this.worktime.holidaySpec.bonusMethod !== HolidayBonusPaymentMethod.None) {
          period.bonusDaysCount = period.period.days.length;
        }
      },

    };
    this.uiHelpers.openEditDialog("salaxy-components/worker/holidays/HolidayYearHolidaysEditDialog.html", item, logic).then((result) => {
       if (result.action === "delete" || result.action === "delete-no-save") {
        this.annualLeavestoBePaid.splice(this.annualLeavestoBePaid.indexOf(item), 1);
      } else {
        // No changes, cancel
      }
      });
  }

  private newGuid(): string {
    let dt = new Date().getTime();
    const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      /* eslint-disable-next-line no-bitwise */
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      /* eslint-disable-next-line no-bitwise */
      return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
    });
    return uuid;
  }
}
